import {useInfiniteQuery, useQueryClient} from '@tanstack/react-query';
import {useState} from 'react';
import {useParams} from 'react-router-dom';

import {fetchRecords} from '../services/PaymentEventsService';

export const usePaymentEvents = (invoiceIdProp?: string) => {
    const params = useParams();
    const invoiceId = invoiceIdProp ?? params.invoiceId ?? params.id;

    const queryClient = useQueryClient();
    const [limit] = useState(10);
    const [keyword, setKeyword] = useState('');

    const sort = {column: 'occurredAt', direction: 'desc' as const};

    const paymentEventsQuery = useInfiniteQuery({
        queryKey: ['payment-events', invoiceId, keyword, limit, sort.column, sort.direction],
        queryFn: ({pageParam}: {pageParam: string | null}) => fetchRecords({
            limit,
            sort,
            nextToken: pageParam,
            filter: invoiceId ? {invoiceId} : undefined,
        }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        enabled: !!invoiceId,
    });

    const fetchRecordsAction = () => {
        if (paymentEventsQuery.hasNextPage) {
            paymentEventsQuery.fetchNextPage();
        }
    };

    const reloadRecords = () => {
        queryClient.invalidateQueries({queryKey: ['payment-events', invoiceId]});
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = paymentEventsQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: paymentEventsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: paymentEventsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        reloadRecords,
        handleFilterByKeyword,
    };
};
